import { useCallback, useContext } from "react";
import { useNavigate } from "react-router";
import { Body, Button, ButtonGroup, H2, H3 } from "@datapay-by-datacom/design-system";
import { FileDropzone } from "../../shared/components";
import { SplitCostingImportRowsTable } from "../components/SplitCostingImportRowsTable";
import { SplitCostingWizardCtx } from "../components/SplitCostingWizardContext";
import { parseSplitCostingCsv } from "../utils";
import styles from "./UploadScreen.module.css";

const VALIDATE_ROUTE = "/onboarding/split-costing-import/validate";

/**
 * Step 1 of the split costing import wizard — Upload & Preview.
 *
 * Rendered as the index child of `SplitCostingImportLayout`. The layout gates
 * access behind `CompanyContextStep`, so this screen only mounts once a valid
 * company code has been validated — no redirect guard needed here.
 *
 * The consultant drops (or browses) a CSV exported from the Split Costing tab.
 * On drop the file is read and parsed client-side:
 * - Parse errors render above the table (no Continue until resolved).
 * - A successful parse shows a preview table and enables Continue.
 *
 * Nothing is sent to the API here — this step is purely client-side (D1).
 */
export function UploadScreen() {
  const wizardCtx = useContext(SplitCostingWizardCtx);
  const navigate = useNavigate();

  if (!wizardCtx) {
    throw new Error("UploadScreen must be rendered inside SplitCostingWizardProvider");
  }

  const { companyCode, companyName, fileName, parsedRows, parseErrors, setUploadSuccess, setUploadErrors, clearUpload } = wizardCtx;

  const hasParseErrors = parseErrors.length > 0;
  const canContinue = parsedRows !== null && parsedRows.length > 0 && !hasParseErrors;

  const handleFileChange = useCallback(
    (file: File | File[] | null) => {
      if (!file || Array.isArray(file)) {
        clearUpload();
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result;
        if (typeof text !== "string") {
          setUploadErrors(file.name, [
            { fileRow: null, message: "Could not read the file. Please try again." },
          ], file, null);
          return;
        }

        const outcome = parseSplitCostingCsv(text);
        if (outcome.ok) {
          setUploadSuccess(file.name, outcome.rows, file, text);
        } else {
          setUploadErrors(file.name, outcome.errors, file, text);
        }
      };
      reader.onerror = () => {
        setUploadErrors(file.name, [
          { fileRow: null, message: "The file could not be read. Please try again." },
        ], file, null);
      };
      reader.onabort = () => {
        setUploadErrors(file.name, [
          { fileRow: null, message: "File reading was interrupted. Please try again." },
        ], file, null);
      };
      reader.readAsText(file);
    },
    [clearUpload, setUploadSuccess, setUploadErrors]
  );

  const handleContinue = useCallback(() => {
    if (canContinue) {
      void navigate(VALIDATE_ROUTE);
    }
  }, [canContinue, navigate]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <H2>Upload split costing CSV</H2>
        <Body>
          Importing for <strong>{companyName ?? companyCode}</strong>.
        </Body>
      </div>

      {/* File drop zone */}
      <section className={styles.section} aria-labelledby="upload-heading">
        <H3 id="upload-heading">Select file</H3>
        <FileDropzone
          value={null}
          onChange={handleFileChange}
          instruction="Drop your CSV file here, or click to browse"
          subInstruction={
            "CSV must contain: Employee Code, Apportioned Percentage, Division, Cost Centre, Department. " +
            "The file must not include an Employee Name column."
          }
          accept=".csv"
          multiple={false}
          buttonText="Select CSV file"
        />
        {fileName && (
          <p className={styles.selectedFile}>
            Selected: <strong>{fileName}</strong>
          </p>
        )}
      </section>

      {/* Parse errors */}
      {hasParseErrors && (
        <section
          className={styles.errorSection}
          aria-labelledby="parse-errors-heading"
          role="alert"
        >
          <H3 id="parse-errors-heading" className={styles.errorHeading}>
            Could not parse this file
          </H3>
          <ul className={styles.errorList}>
            {parseErrors.map((err, i) => (
              <li key={i} className={styles.errorItem}>
                {err.fileRow !== null && (
                  <span className={styles.errorRow}>Row {err.fileRow}: </span>
                )}
                {err.message}
              </li>
            ))}
          </ul>
          <Body className={styles.errorHint}>
            Fix the issues above and re-upload the file.
          </Body>
        </section>
      )}

      {/* Preview table */}
      {parsedRows !== null && parsedRows.length > 0 && (
        <section className={styles.section} aria-labelledby="preview-heading">
          <H3 id="preview-heading">Preview ({parsedRows.length} rows)</H3>
          <SplitCostingImportRowsTable
            rows={parsedRows}
            ariaLabel="Parsed split costing rows"
          />
        </section>
      )}

      {/* Actions */}
      <ButtonGroup align="right">
        <Button
          variant="primary"
          isDisabled={!canContinue}
          onPress={handleContinue}
        >
          Continue
        </Button>
      </ButtonGroup>
    </div>
  );
}
